import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { NewsletterComposer, CampaignHistory } from '../../components/admin/NewsletterComposer';
import { CampaignBroadcast } from '../../components/admin/CampaignBroadcast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNewsletter = () => {
  const { isStaff, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canAccess = isStaff && hasPermission('featured');
  const [candidates, setCandidates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [recipients, setRecipients] = useState({ recipients: [], total: 0, with_email: 0 });
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, h, r] = await Promise.all([
        axios.get(`${API_URL}/api/admin/newsletter/candidates?days=30`),
        axios.get(`${API_URL}/api/admin/newsletter/campaigns`),
        axios.get(`${API_URL}/api/admin/newsletter/recipients`),
      ]);
      setCandidates(c.data); setCampaigns(h.data); setRecipients(r.data);
    } catch {
      toast.error('Impossible de charger la newsletter');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) { navigate('/connexion'); return; }
    load();
  }, [canAccess, navigate, load]);

  const openCampaign = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/newsletter/campaigns/${id}`);
      setCurrent(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Campagne introuvable');
    }
  };

  const createCampaign = async (payload) => {
    const res = await axios.post(`${API_URL}/api/admin/newsletter/campaigns`, payload);
    toast.success('Affiche générée');
    await load();
    await openCampaign(res.data.id);
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Tableau de bord
        </Link>
        <div className="mb-8">
          <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Megaphone className="w-4 h-4" /> Diffusion</p>
          <h1 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Newsletter Nouveautés</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {recipients.total} client(s) inscrit(s) · {recipients.with_email} avec e-mail
          </p>
        </div>

        {current ? (
          <CampaignBroadcast campaign={current} recipients={recipients.recipients}
            onBack={() => setCurrent(null)} onRefresh={() => openCampaign(current.id)} />
        ) : loading ? (
          <p className="text-zinc-500 text-sm">Chargement...</p>
        ) : (
          <>
            <NewsletterComposer candidates={candidates} onCreate={createCampaign} />
            <CampaignHistory campaigns={campaigns} onOpen={openCampaign} />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminNewsletter;
